/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import AbstractModule from "../abstract_module";
import IGroundPlaneModule from "./scene_ground_plane_interface";
import {BoundingBox, Debug, RenderDocument, WebGLContext} from "js_renderer";
import {vec2, vec3} from "gl-matrix";
import GroundPlane from "../../elements/ground_plane";
import Scene from "../../scene";
// @ts-ignore
import {EGroundTypes} from "./scene_ground_plane_enums.ts";
import Settings from "../settings";
import IModel from "../../structural_elements/model_interface";

interface IGrassSettings {
    animate?: boolean;  // Grass waving
    scale?: number;
    density?: number;
}

interface IGroundPlaneSettings {
    enabled?: boolean;
    scale?: number;
    type?: EGroundTypes;
    color?: vec3; // values in range <0, 255>
    maxY?: number;
    grass?: IGrassSettings;
}

export default
class GroundPlaneModule extends AbstractModule implements IGroundPlaneModule {

    protected static _typeName: string = "groundPlane";

    protected _defaultSettings: IGroundPlaneSettings;
    protected _settings: IGroundPlaneSettings;

    private _sapiModel: IModel = null;
    private _SAPIElement: GroundPlane = null;
    private _sapiGroundBottomCapElement: null;

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        this._type = GroundPlaneModule._typeName;

        this._defaultSettings = {
            enabled: false,     // Create/delete from model
            scale: 2,
            type: EGroundTypes.COLOR,            // number/enum: 0: color, 1: texture, 2: grass
            color: vec3.fromValues(82, 205, 29),
            maxY: 0,
            grass: {
                animate: true,  // Grass waving
                scale: 0.1,
                density: 1
            }
        };

        this._settings = Settings.clone(this._defaultSettings);

        this.sceneAPI.activityModule.registerObserver(this);
        this.renderDocument.registerObserver(this);
    }

    public static getClassType(): string {
        return GroundPlaneModule._typeName;
    }

    public isGroundPlaneEnabled(): boolean {
        return this._settings.enabled;
    }

    public getGroundPlaneType(): EGroundTypes {
        return this._settings.type;
    }

    public getGroundPlaneScale(): number {
        return this._settings.scale;
    }

    public getGroundPlaneColor(): vec3 {
        return vec3.clone(this._settings.color);
    }

    public isGroundPlaneGrassAnimating(): boolean {
        return this._settings.grass.animate;
    }

    public onManipulatorChanged() {
        this.invalidateFloorPlanTexture();
    }

    public onBoundingBoxUpdated(aBBox: BoundingBox, aBBoxForNPC: BoundingBox) {
        this.invalidateFloorPlanTexture();
    }

    public invalidateFloorPlanTexture() {
        // TODO: Jarek we should not make difference between the render graphs here.
        /*let webglRenderGraph = this.renderDocument.getRenderer().getWebGLRenderGraph();
        if (webglRenderGraph) {
            webglRenderGraph.getLayer('environment').invalidateTexture();
        }*/
    }

    public viewChanged() {
        //
    }

    public viewResized(aContentSize: vec2) {
        //
    }

    public setFromModelDescription(aJsonObject: any) {
        let settings: IGroundPlaneSettings = {};
        if (aJsonObject.hasOwnProperty(GroundPlaneModule._typeName)) {
            settings = aJsonObject[GroundPlaneModule._typeName];
        }
        this.set(settings);
    }

    protected applySettings(aChangedSettings: any, aPreviousSettings: any) {

        if (aChangedSettings.hasOwnProperty("grass")) {
            if (aChangedSettings.grass.hasOwnProperty("animate")) {
                if (aChangedSettings.grass.animate) {
                    this.sceneAPI.viewModeModule.scheduleRedraw();
                }
            }
        }

        if (aChangedSettings.hasOwnProperty("color")) {
            if (this._SAPIElement) {
                this._SAPIElement.setColor(this._settings.color);
            }
            this.sceneAPI.viewModeModule.scheduleRedraw();
        }

        if (aChangedSettings.hasOwnProperty("type") ||
           (aChangedSettings.hasOwnProperty("scale"))) {

            // check scale limits, range <1, 10>
            if (aChangedSettings.hasOwnProperty("scale")) {
                if (aChangedSettings.scale < 1) {
                    this._settings.scale = 1;
                    aChangedSettings.scale = 1;
                }
                if (aChangedSettings.scale > 10) {
                    this._settings.scale = 10;
                    aChangedSettings.scale = 10;
                }
            }

            if (aChangedSettings.type === EGroundTypes.GRASS && !WebGLContext.getFeatureAvailable("INSTANCING")) {
                Debug.logWarning("Instancing is not available for the grass rendering, so instead only textured ground plane will be rendered.");
                this._settings.type = EGroundTypes.TEXTURE;
            }

            if (this._settings.enabled) {
                this._createGround(); // recreate the ground element
            }

            this.sceneAPI.viewModeModule.scheduleRedraw();
        }

        if (aChangedSettings.hasOwnProperty("enabled")) {
            this.setEnabled();
        }

    }

    /**
     * @param {boolean} aEnabled
     */
    protected setEnabled() {

        if (this._settings.enabled) {
            this._createGround();
        }
        else {
            this._deleteGround();
        }

        this.sceneAPI.viewModeModule.scheduleRedraw();
    }

    private _createGround() {
        if (this._sapiModel) {
            this._deleteGround();
        }

        // Create new Ground Plane model
        this._sapiModel = this.sceneAPI.getDocument().createModel("Ground Plane", null, null, false, false);
        this._sapiModel.disableManipulation();
        this._sapiModel.setSelectionAllowed(false);

        // Create new group
        let groundGroupID = this._sapiModel.addGroup(null, "Ground", true);

        // Create new element
        // Create ground element
        this._SAPIElement = new GroundPlane(this.renderDocument);

        let bb = this.sceneAPI.getDocument().getSBoundingBoxFromVisibleObjectsForNPC();
        this._SAPIElement.set(true, bb, this._settings.scale);

        this._SAPIElement.useTexture(this._settings.type !== EGroundTypes.COLOR);

        this._SAPIElement.setColor(this._settings.color);

        this._sapiModel.addElement(this._SAPIElement, groundGroupID);

        // Create ground bottom
        /*    this._settings.sapiGroundBottomCapElement = new cadwork.webgl.SceneAPI.elements.GroundPlane(this.sceneAPI);

            this.sceneAPI.getDocument().attachElementToInternalModel(this._settings.sapiGroundBottomCapElement);

            this._settings.sapiGroundBottomCapElement.set(false, bb, this._settings.scale);

            if (this._settings.sapiGroundBottomCapElement.type > 0) {
                // Texture, Grass
                this._settings.sapiGroundBottomCapElement.useTexture(true);
            } else {
                // Color
                this._settings.sapiGroundBottomCapElement.useTexture(false);
                this._settings.sapiGroundBottomCapElement.setColor(0.0, 1.0, 0.0);
            }
        */
        // this._settings.sapiGroundBottomCapElement.draw();

        // Save ground information
        // this.sceneAPI.groundPlaneModule.setGroundPlaneMaxY(this._ground._max[1]);

        // Add element to model into group
        // this._model.addElement(this._ground, groundGroupID);
    }

    private _deleteGround() {
        if (this._sapiModel) {
            this._sapiModel.remove();
            this._sapiModel = null;
        }
    }
}

Scene.registerSceneAPIModule(GroundPlaneModule);
